import React, { useState, useEffect, useRef } from 'react';
import { NutritionJournal } from '@/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Droplets, Check, Plus, Minus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import LisaAdvice from './LisaAdvice';
import { Progress } from '@/components/ui/progress';
import useUserProfileSync from '@/hooks/useUserProfileSync';
import { 
  ModernTable, 
  ModernTableBody, 
  ModernTableRow, 
  ModernTableCell,
  ModernFoodItemCell 
} from '@/components/ui/modern-table';

interface NutritionTabProps {
  journal: NutritionJournal;
  setJournal: (journal: NutritionJournal) => void;
  containerVariants: any;
  itemVariants: any;
}

// Control de actualización para evitar ciclos de actualización
const updateState = {
  isUpdating: false,
  lastUpdateTime: 0,
  updateDebounceTimeout: null as NodeJS.Timeout | null,
  initialSyncComplete: false
};

const NutritionTab: React.FC<NutritionTabProps> = ({
  journal,
  setJournal,
  containerVariants,
  itemVariants
}) => {
  
  const [waterIntake, setWaterIntake] = useState<number>(0);
  const [showWaterSuccess, setShowWaterSuccess] = useState(false);
  const { user } = useAuth();
  
  const { getSyncedProfile, refetch } = useUserProfileSync({
    userId: user?.id,
    enabled: !!user?.id,
    onProfileUpdated: (profile) => {
      if (profile && journal && !updateState.initialSyncComplete && !updateState.isUpdating) {
        updateState.isUpdating = true;
        
        const updatedJournal = { 
          ...journal,
          nutritionSummary: {
            ...journal.nutritionSummary,
            goalCalories: parseFloat(String(profile.targetCalories)) || journal.nutritionSummary?.goalCalories,
            goalProtein: parseFloat(String(profile.targetProtein)) || journal.nutritionSummary?.goalProtein,
            goalCarbs: parseFloat(String(profile.targetCarbs)) || journal.nutritionSummary?.goalCarbs,
            goalFat: parseFloat(String(profile.targetFat)) || journal.nutritionSummary?.goalFat,
            goalIron: parseFloat(String(profile.targetIron)) || journal.nutritionSummary?.goalIron,
            goalCalcium: parseFloat(String(profile.targetCalcium)) || journal.nutritionSummary?.goalCalcium,
            goalZinc: parseFloat(String(profile.targetZinc)) || journal.nutritionSummary?.goalZinc,
            goalMagnesium: parseFloat(String(profile.targetMagnesium)) || journal.nutritionSummary?.goalMagnesium,
            goalOmega3: parseFloat(String(profile.targetOmega3)) || journal.nutritionSummary?.goalOmega3
          }
        };
        
        // Check if there are actual changes before updating
        const hasChanges = JSON.stringify(updatedJournal.nutritionSummary) !== JSON.stringify(journal.nutritionSummary);
        
        if (hasChanges) {
          window.dispatchEvent(new CustomEvent('NUTRITION_PROFILE_UPDATED', {
            detail: {
              nutritionGoals: {
                calories: profile.targetCalories,
                protein: profile.targetProtein,
                carbs: profile.targetCarbs,
                fat: profile.targetFat,
                iron: profile.targetIron,
                calcium: profile.targetCalcium,
                zinc: profile.targetZinc,
                magnesium: profile.targetMagnesium,
                omega3: profile.targetOmega3
              }
            }
          }));
          
          setJournal(updatedJournal);
        }
        
        updateState.initialSyncComplete = true;
        
        setTimeout(() => {
          updateState.isUpdating = false;
        }, 600);
      }
    }
  });
  
  // Función centralizada para manejar actualizaciones
  const handleProfileUpdate = () => {
    if (updateState.isUpdating) return;
    
    // Limpiar cualquier actualización pendiente
    if (updateState.updateDebounceTimeout) {
      clearTimeout(updateState.updateDebounceTimeout);
      updateState.updateDebounceTimeout = null;
    }
    
    const now = Date.now();
    const timeSinceLastUpdate = now - updateState.lastUpdateTime;
    
    // Aplicar debounce de al menos 600ms
    if (timeSinceLastUpdate < 600) {
      updateState.updateDebounceTimeout = setTimeout(() => {
        performProfileSync();
      }, 600);
      return;
    }
    
    performProfileSync();
  };
  
  const performProfileSync = () => {
    if (!user?.id || !journal || updateState.isUpdating) return;
    
    updateState.isUpdating = true;
    updateState.lastUpdateTime = Date.now();
    
    refetch();
    
    const profile = getSyncedProfile();
    if (profile && journal) {
      const updatedJournal = { 
        ...journal,
        nutritionSummary: {
          ...journal.nutritionSummary,
          goalCalories: parseFloat(String(profile.targetCalories)) || journal.nutritionSummary?.goalCalories,
          goalProtein: parseFloat(String(profile.targetProtein)) || journal.nutritionSummary?.goalProtein,
          goalCarbs: parseFloat(String(profile.targetCarbs)) || journal.nutritionSummary?.goalCarbs,
          goalFat: parseFloat(String(profile.targetFat)) || journal.nutritionSummary?.goalFat,
          goalIron: parseFloat(String(profile.targetIron)) || journal.nutritionSummary?.goalIron,
          goalCalcium: parseFloat(String(profile.targetCalcium)) || journal.nutritionSummary?.goalCalcium,
          goalZinc: parseFloat(String(profile.targetZinc)) || journal.nutritionSummary?.goalZinc,
          goalMagnesium: parseFloat(String(profile.targetMagnesium)) || journal.nutritionSummary?.goalMagnesium,
          goalOmega3: parseFloat(String(profile.targetOmega3)) || journal.nutritionSummary?.goalOmega3
        }
      };
      
      // Only update if there are actual changes
      if (JSON.stringify(updatedJournal.nutritionSummary) !== JSON.stringify(journal.nutritionSummary)) {
        setJournal(updatedJournal);
      }
    }
    
    // Dar tiempo suficiente antes de permitir otra actualización
    setTimeout(() => {
      updateState.isUpdating = false;
    }, 1000);
  };
  
  useEffect(() => {
    if (journal) {
      setWaterIntake(journal.waterIntake || 0);
      setShowWaterSuccess(journal.waterIntake >= 6);
    }
  }, [journal?.waterIntake]);

  useEffect(() => {
    let isMounted = true;
    
    // Use a more efficient approach to handle multiple events with the same handler
    const handleEvent = () => {
      if (isMounted && !updateState.isUpdating) {
        handleProfileUpdate();
      }
    };
    
    // List of events we want to listen to
    const events = [
      'profile-updated',
      'weight-updated',
      'FORCE_MENU_UPDATE',
    ];
    
    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleEvent);
    });
    
    return () => {
      isMounted = false;
      
      // Remove event listeners
      events.forEach(event => {
        window.removeEventListener(event, handleEvent);
      });
      
      if (updateState.updateDebounceTimeout) {
        clearTimeout(updateState.updateDebounceTimeout);
      }
    };
  }, [journal]);

  const handleAddWater = () => {
    if (updateState.isUpdating || !journal) return;
    
    updateState.isUpdating = true;
    const newWaterIntake = Math.min(12, waterIntake + 1);
    setWaterIntake(newWaterIntake);
    
    const updatedJournal = { ...journal, waterIntake: newWaterIntake };
    setJournal(updatedJournal);
    
    if (newWaterIntake >= 6 && !showWaterSuccess) {
      setShowWaterSuccess(true);
      toast.success("¡Felicidades! Has alcanzado tu objetivo de hidratación diaria.");
    }
    
    setTimeout(() => {
      updateState.isUpdating = false;
    }, 300);
  };
  
  const handleRemoveWater = () => {
    if (updateState.isUpdating || waterIntake <= 0 || !journal) return;
    
    updateState.isUpdating = true;
    const newWaterIntake = Math.max(0, waterIntake - 1);
    setWaterIntake(newWaterIntake);
    
    const updatedJournal = { ...journal, waterIntake: newWaterIntake };
    setJournal(updatedJournal);
    
    if (newWaterIntake < 6 && showWaterSuccess) {
      setShowWaterSuccess(false);
    }
    
    setTimeout(() => {
      updateState.isUpdating = false;
    }, 300);
  };

  const renderFoodItems = () => {
    if (!journal?.meals || journal.meals.length === 0) {
      return null; // Don't render anything if no meals
    }

    return (
      <ModernTable>
        <ModernTableBody>
          {journal.meals.map((meal, index) => (
            <ModernTableRow key={`meal-${index}`}>
              <ModernFoodItemCell 
                name={meal.name} 
                quantity={meal.quantity || 100} 
                unit={meal.unit || "g"}
              />
              <ModernTableCell className="text-right w-20">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-purple-300 hover:text-purple-100 hover:bg-purple-800/50"
                  onClick={() => {
                    if (updateState.isUpdating) return;
                    
                    updateState.isUpdating = true;
                    const updatedJournal = {...journal};
                    updatedJournal.meals = updatedJournal.meals.filter((_, i) => i !== index);
                    setJournal(updatedJournal);
                    
                    setTimeout(() => {
                      updateState.isUpdating = false;
                    }, 300);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </ModernTableCell>
            </ModernTableRow>
          ))}
        </ModernTableBody>
      </ModernTable>
    );
  };

  if (!journal) {
    return <div className="p-4 text-center text-gray-500">No se encontraron datos del diario nutricional.</div>;
  }

  // Check if there are meals to show the card
  const hasMeals = journal?.meals && journal.meals.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <LisaAdvice journal={journal} user={user} />
      </div>

      {hasMeals && (
        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-100">
          <CardHeader>
            <CardTitle className="text-purple-800">Alimentos registrados</CardTitle>
            <CardDescription className="text-purple-600">
              Tus alimentos añadidos hoy
            </CardDescription>
          </CardHeader>
          <CardContent>
            {renderFoodItems()}
          </CardContent>
        </Card>
      )}

      <div>
        <Card className="overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Droplets className="h-6 w-6 text-blue-500" />
              Hidratación
            </CardTitle>
            <CardDescription className="text-blue-600">
              Registra tu consumo de agua durante el día
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <Progress 
                    value={(waterIntake / 6) * 100} 
                    className="h-3"
                    indicatorClassName="bg-blue-500"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={handleRemoveWater}
                    disabled={waterIntake <= 0}
                    className="h-9 w-9 border-blue-200 hover:bg-blue-50"
                  >
                    <Minus className="h-4 w-4 text-blue-600" />
                  </Button>
                  <span className="w-20 text-center font-semibold text-blue-800">
                    {waterIntake} / 6 vasos
                  </span>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={handleAddWater}
                    disabled={waterIntake >= 12}
                    className="h-9 w-9 border-blue-200 hover:bg-blue-50"
                  >
                    <Plus className="h-4 w-4 text-blue-600" />
                  </Button>
                </div>
              </div>
              
              {showWaterSuccess && (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                  <Check className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">¡Has alcanzado tu objetivo de hidratación!</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NutritionTab;
